import { useMemo } from 'react'
import { ReportingAccessLevels } from '../../NonReact/constants/Permissions'
import { SERVER_ALL_VALUE, SERVER_NONE_VALUE } from '../../React/AddUser/Constants'
import { useAppLicenceFeature } from '../../React/Common/Hooks/useAppLicenceFeature'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import { RxJSCurrentState, useRxJSState } from '../../React/Common/Hooks/useRxJSState'
import { currentUserState$ } from '../../NonReact/common/RxJS/UserInfoState'
import { AdminUserResponse } from '../../NonReact/common/Types/Types'

/**
 * A hook that determines whether the insights hub is available to the user.
 *
 * @param ignoreInsightsHubHiddenCheck - Whether to ignore the Insights Hub hidden check.
 */
export const useIsInsightsAvailable = (ignoreInsightsHubHiddenCheck?: boolean): Definable<boolean> => {
    const [userInfo]: RxJSCurrentState<AdminUserResponse> = useRxJSState(currentUserState$)

    const insightsHubEnabled: Nullable<boolean> = useAppLicenceFeature('FeatureCapsInsightsHubPage')

    return useMemo((): Definable<boolean> => {
        if (IsVarEmpty(userInfo) || IsVarEmpty(insightsHubEnabled)) {
            return undefined
        }

        let shouldShowInsightsHubMenu: boolean = insightsHubEnabled ?? false

        // If feature is enabled then check whether the user types allows the insights hub.
        shouldShowInsightsHubMenu =
            shouldShowInsightsHubMenu &&
            (userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.ENTERPRISE ||
                userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR ||
                userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.CXA_ADVANCED ||
                userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.CXE_ADVANCED ||
                userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.ONE_THOUSAND ||
                userInfo?.Scope?.ReportingAccess === ReportingAccessLevels.TWO_THOUSAND)

        // Determine whether user has scope permission lower than Partition.
        // Insights page should be shown to users with permission at Partition or higher level only.
        const deviceNumber: string = userInfo?.Scope.DeviceNumber ?? SERVER_ALL_VALUE
        const agentNumber: string = userInfo?.Scope.AgentNumber ?? SERVER_ALL_VALUE

        if (
            (deviceNumber !== SERVER_ALL_VALUE && deviceNumber !== SERVER_NONE_VALUE) ||
            (agentNumber !== SERVER_ALL_VALUE && agentNumber !== SERVER_NONE_VALUE)
        ) {
            shouldShowInsightsHubMenu = false
        }

        // Check if the user has hidden the Insights Hub.
        if (userInfo?.InsightsHubHidden && !ignoreInsightsHubHiddenCheck) {
            shouldShowInsightsHubMenu = false
        }

        return shouldShowInsightsHubMenu
    }, [userInfo, insightsHubEnabled])
}
